import type { SqliteDatabase } from './database.js';

export type UploadStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ERROR';

export interface OutboxRow {
  clientOpId: string;
  kind: 'sale' | 'refund';
  cartId: string;
  uploadStatus: UploadStatus;
  failedAttempts: number;
  payloadJson: string;
  localReceiptId: string | null;
  createdAt: string;
  lastError: string | null;
}

const MAX_ATTEMPTS = 6;

export function enqueueOutboxOp(
  db: SqliteDatabase,
  op: {
    clientOpId: string;
    kind: 'sale' | 'refund';
    cartId: string;
    payloadJson: string;
    localReceiptId?: string;
  },
): void {
  db.prepare(
    `INSERT INTO outbox_ops (
      client_op_id, kind, cart_id, upload_status, failed_attempts,
      payload_json, local_receipt_id, created_at
    ) VALUES (?, ?, ?, 'IN_PROGRESS', 0, ?, ?, ?)`,
  ).run(
    op.clientOpId,
    op.kind,
    op.cartId,
    op.payloadJson,
    op.localReceiptId ?? null,
    new Date().toISOString(),
  );
}

export function listPendingOutboxOps(db: SqliteDatabase): OutboxRow[] {
  return db
    .prepare(
      `SELECT client_op_id as clientOpId, kind, cart_id as cartId,
              upload_status as uploadStatus, failed_attempts as failedAttempts,
              payload_json as payloadJson, local_receipt_id as localReceiptId,
              created_at as createdAt, last_error as lastError
       FROM outbox_ops
       WHERE upload_status IN ('PENDING', 'IN_PROGRESS')
       ORDER BY created_at ASC`,
    )
    .all() as OutboxRow[];
}

export function markOutboxCompleted(db: SqliteDatabase, clientOpId: string): void {
  db.prepare(
    `UPDATE outbox_ops SET upload_status = 'COMPLETED', last_error = NULL WHERE client_op_id = ?`,
  ).run(clientOpId);
}

export function markOutboxPending(
  db: SqliteDatabase,
  clientOpId: string,
  error?: string,
): void {
  const row = db
    .prepare(`SELECT failed_attempts FROM outbox_ops WHERE client_op_id = ?`)
    .get(clientOpId) as { failed_attempts: number } | undefined;
  const attempts = (row?.failed_attempts ?? 0) + 1;
  const status = attempts >= MAX_ATTEMPTS ? 'ERROR' : 'PENDING';
  db.prepare(
    `UPDATE outbox_ops SET upload_status = ?, failed_attempts = ?, last_error = ? WHERE client_op_id = ?`,
  ).run(status, attempts, error ?? null, clientOpId);
}

export function removeOutboxOp(db: SqliteDatabase, clientOpId: string): void {
  db.prepare(`DELETE FROM outbox_ops WHERE client_op_id = ?`).run(clientOpId);
}

export function pendingOutboxCount(db: SqliteDatabase): number {
  const row = db
    .prepare(
      `SELECT COUNT(*) as c FROM outbox_ops WHERE upload_status IN ('PENDING', 'IN_PROGRESS')`,
    )
    .get() as { c: number };
  return row.c;
}

export function resetInProgressToPending(db: SqliteDatabase): void {
  db.prepare(
    `UPDATE outbox_ops SET upload_status = 'PENDING' WHERE upload_status = 'IN_PROGRESS'`,
  ).run();
}
